import CustomForm from "../../CustomForm";
import ICustomFormSettings from "../../ICustomFormSettings";
import m, {Vnode} from "mithril";
import SuggestionBox from "../../shared/components/SuggestionBox/SuggestionBox";
import {loadJsonFromLabel, loadJsonFromString} from "../../shared/helpers";
import {LabelItem} from "../../../model/items/labelItem";
import {Item} from "../../shared/components/SuggestionBox/source/ItemsSuggestionSource";
import {TextboxItem} from "../../../model/items/textbox";
import {CheckboxItem} from "../../../model/items/checkbox";
import CheckboxCell from "../../shared/components/FormCell/CheckboxCell";

type SuggestionSetting = {
    minLength?: number,
    maxResults?: number,
}

export default class TextSuggestion extends CustomForm {

    private suggestionBox?: SuggestionBox;
    private suggestionSettings: SuggestionSetting = {};
    private readonly checkboxes: CheckboxCell[] = [];

    private readonly outputId: TextboxItem;
    private readonly outputGroups: TextboxItem[] = [];
    private readonly outputItem: TextboxItem;

    private errorMessage = '';

    constructor(settings: ICustomFormSettings) {
        super(settings);

        const layout = settings.form.grid.layout;

        this.outputId = <TextboxItem>layout[2][1].cellItems[0];
        this.outputGroups[0] = <TextboxItem>layout[3][1].cellItems[0];
        this.outputItem = <TextboxItem>layout[4][1].cellItems[0];

        let i = 5;
        while (true) {
            const outputGroup = <TextboxItem|undefined>layout[i]?.[1]?.cellItems[0];
            if (outputGroup instanceof TextboxItem) {
                this.outputGroups.push(outputGroup);
            } else {
                break;
            }
            i++;
        }

        loadJsonFromLabel(<LabelItem>layout[0][1].cellItems[0], s => this.suggestionSettings = s)
        if (this.settings.form.aux1) {
            loadJsonFromString(this.settings.form.aux1, this.settings.form.questionary, s => this.suggestionSettings = s);
        }

        const loadItems = () => loadJsonFromLabel(<LabelItem>layout[1][1].cellItems[0], (items) => {
            this.suggestionBox = new SuggestionBox(
                items,
                (item) => {
                    this.outputId.set(item.id);
                    this.outputItem.set(item.item);

                    const additionalValues = [...(item.groups || []), ...(item.values || [])];
                    this.outputGroups.forEach((g, i) => g.set(i in additionalValues ? additionalValues[i] : ''));
                },
                this.getItem(),
            );
            this.suggestionBox.setAutocompleteMinLength(this.suggestionSettings.minLength ?? 3);
            if (!this.getItem() && !this.settings.form.activeGlobalUnknown) {
                this.suggestionBox.focus();
            }

            this.suggestionBox.suggestionSource.setMaxResults(this.suggestionSettings.maxResults ?? 10);

            m.redraw();
        }, () => {
            this.errorMessage = 'Loading failed. Try again in 5 seconds.'
            setTimeout(() => {
                this.errorMessage = '';
                m.redraw();
                loadItems();
            }, 5000);
            m.redraw()
        });
        loadItems();

        for (const cell of settings.form.grid.cells) {
            for (const cellItem of cell.cellItems) {
                if (cellItem instanceof CheckboxItem) {
                    this.checkboxes.push(new CheckboxCell(
                        cell,
                        (ci, v) => {
                            this.onSetValue(ci, v);
                            if (!this.outputId.getVariable()) { // reset suggestion
                                this.suggestionBox?.reset();
                            }
                        },
                    ));
                }
            }
        }

    }



    checkRequirements(): boolean {
        if (!this.outputId) {
            console.warn(`customForm-Text-suggestion: Item on row 2 and col 1 must be a Textbox for id`);
            return false;
        }
        if (!this.outputGroups[0]) {
            console.warn(`customForm-Text-suggestion: Item on row 3 and col 1 must be a Textbox for group`);
            return false;
        }
        if (!this.outputItem) {
            console.warn(`customForm-Text-suggestion: Item on row 4 and col 1 must be a Textbox for item`);
            return false;
        }

        return true;
    }

    getItem(): Item|null {
        if (this.outputId.getVariable()) {
            return {
                id: this.outputId.getVariable(),
                item: this.outputItem.getVariable(),
                groups: this.outputGroups.map(g => g.getVariable()),
            };
        }
        return null;
    }

    public view(): Vnode {
        return this.viewLayout([
            this.viewHeroVideo(),
            this.viewHeroImage(),
            this.suggestionBox ? m(this.suggestionBox) : this.viewLoading(),
            this.errorMessage ? m('.text-center', {}, this.errorMessage) : null,
            m('.', {}, this.checkboxes.map(cell => m(cell))),
        ]);
    }

    private viewLoading(): Vnode {
        return m('.loading', {}, [
            m('.icon.icon-cached.spinning', {title: "Loading"}),
        ]);
    }
}
